import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const EMPTY = {
  slug: "", name: "", dosage: "", category: "peptide", accent: "#111111",
  price: 0, stock: 100, image_url: "", short_desc_nl: "", short_desc_en: "",
  long_desc_nl: "", long_desc_en: "", active: true,
};

function BunqPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/bunq/status");
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Bunq status");
    }
  };

  useEffect(() => { load(); }, []);

  const doAction = async (path, label, params = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(path, null, { params });
      toast.success(`${label}: ${JSON.stringify(data)}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${label} failed`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bunq-panel" className="border border-neutral-300 bg-white p-6 md:p-10">
      <h2 className="font-serif-display text-3xl mb-2">Bunq · Payment Sync</h2>
      <p className="text-sm text-neutral-600 mb-8">
        Automatically match incoming SEPA bank transfers to open orders by reference (<code>PV-XXXX</code>) and amount.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-l border-neutral-300 mb-8">
        {[
          ["API key", status?.api_key_configured ? "✓ SET" : "✗ MISSING"],
          ["User ID", status?.context?.user_id || "—"],
          ["Account ID", status?.context?.monetary_account_id || "—"],
          ["IBAN", status?.context?.monetary_iban || "—"],
        ].map(([l, v]) => (
          <div key={l} className="border-r border-b border-neutral-300 p-5">
            <p className="font-mono-label text-[10px] text-neutral-500">{l.toUpperCase()}</p>
            <p className="font-serif-display text-xl mt-1">{v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <button
          data-testid="bunq-setup"
          disabled={busy || !status?.api_key_configured}
          onClick={() => doAction("/admin/bunq/setup", "Setup")}
          className="btn-sharp disabled:opacity-40"
        >
          {status?.context ? "Refresh Bunq context" : "Run initial setup"}
        </button>
        <button
          data-testid="bunq-setup-force"
          disabled={busy || !status?.api_key_configured}
          onClick={() => doAction("/admin/bunq/setup", "Force setup", { force: true })}
          className="btn-sharp disabled:opacity-40 text-[#D12A2F] border-[#D12A2F]"
        >
          Force re-init (new keypair)
        </button>
        <button
          data-testid="bunq-sync"
          disabled={busy || !status?.context?.monetary_account_id}
          onClick={() => doAction("/admin/bunq/sync", "Sync")}
          className="btn-solid disabled:opacity-40"
        >
          {busy ? "Syncing…" : "Sync Bunq now →"}
        </button>
      </div>

      <div className="border-t border-neutral-300 pt-6 text-sm text-neutral-600 space-y-2">
        <p><strong>Workflow:</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Add <code>BUNQ_API_KEY</code> to backend environment (only backend can see it).</li>
          <li>Click <em>Run initial setup</em> — performs the 3-step Bunq handshake and detects your monetary account.</li>
          <li>Click <em>Sync Bunq now</em> — fetches recent payments and marks matching orders as paid.</li>
          <li>Set up a cron (every 15 min) hitting <code>POST /api/admin/bunq/sync</code>.</li>
        </ol>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);

  const loadAll = async () => {
    const [p, o, s] = await Promise.all([
      api.get("/admin/products"),
      api.get("/admin/orders"),
      api.get("/admin/stats"),
    ]);
    setProducts(p.data);
    setOrders(o.data);
    setStats(s.data);
  };

  useEffect(() => { if (user?.role === "admin") loadAll(); }, [user]);

  if (loading) return <div className="pt-40 text-center font-mono-label text-xs">LOADING…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" />;

  const save = async () => {
    try {
      const payload = { ...editing, price: parseFloat(editing.price), stock: parseInt(editing.stock) };
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Created");
      }
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/admin/products/${id}`);
    loadAll();
  };

  return (
    <div data-testid="admin-page" className="pt-32 pb-24 bg-[#F7F7F6] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <p className="font-mono-label text-[11px] text-neutral-500 mb-8">[ ADMIN CONSOLE ]</p>
        <h1 className="font-serif-display font-light text-5xl md:text-6xl mb-12">Admin.</h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-neutral-300 mb-12">
            {[
              ["Revenue", `€${stats.revenue.toFixed(2)}`],
              ["Paid orders", stats.paid_orders],
              ["Customers", stats.users],
              ["Products", stats.products],
            ].map(([l, v]) => (
              <div key={l} data-testid={`stat-${l.toLowerCase().replace(/\s/g, "-")}`} className="border-r border-b border-neutral-300 p-8 bg-white">
                <p className="font-mono-label text-[10px] text-neutral-500">{l.toUpperCase()}</p>
                <p className="font-serif-display text-4xl mt-2">{v}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3 md:gap-6 mb-6 border-b border-neutral-300 overflow-x-auto whitespace-nowrap -mx-4 px-4 md:mx-0 md:px-0">
          {[
            ["orders", "BESTELLINGEN", "ORDERS"],
            ["administratie", "ADMINISTRATIE", "BOEKHOUD"],
            ["vip", "VIP · KLANTEN", "VIP"],
            ["codes", "KORTINGSCODES", "CODES"],
            ["sale", "SALE", "SALE"],
            ["products", "PRODUCTEN", "PRODUCTS"],
            ["bunq", "BUNQ", "BUNQ"],
          ].map(([k, label, short]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className={`font-mono-label text-[10px] md:text-[11px] pb-3 border-b-2 shrink-0 ${tab === k ? "border-black" : "border-transparent text-neutral-500"}`}
            >
              <span className="md:hidden">{short}</span>
              <span className="hidden md:inline">{label}</span>
            </button>
          ))}
        </div>

        {tab === "bunq" && <BunqPanel />}
        {tab === "administratie" && <AccountingPanel />}
        {tab === "codes" && <DiscountCodesPanel />}
        {tab === "sale" && <SalePanel />}
        {tab === "vip" && <VipPanel />}

        {tab === "products" && (
          <div>
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
              <div>
                <p className="font-mono-label text-[10px] text-neutral-500 mb-1">[ PRODUCTEN · {products.length} ]</p>
                <h2 className="font-serif-display text-3xl">Beheer je catalogus</h2>
              </div>
              <button
                data-testid="admin-new-product"
                onClick={() => setEditing({ ...EMPTY })}
                className="btn-solid text-sm"
              >
                + NIEUW PRODUCT
              </button>
            </div>
            <div className="border-t border-neutral-300">
              {products.map((p) => (
                <div key={p.id} className="border-b border-neutral-300 py-4 flex flex-col md:grid md:grid-cols-12 md:gap-4 md:items-center">
                  {/* Mobile: image + name row */}
                  <div className="flex items-center gap-3 md:contents">
                    <img src={p.image_url} alt="" className="w-14 h-14 object-contain md:col-span-1 md:h-12 md:w-auto" />
                    <div className="flex-1 min-w-0 md:col-span-3">
                      <p className="font-serif-display text-lg md:text-xl leading-tight">{p.name}</p>
                      <p className="font-mono-label text-[10px] text-neutral-500 truncate">{p.slug}</p>
                    </div>
                  </div>
                  {/* Mobile: meta row */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 md:mt-0 md:contents">
                    <span className="font-mono-label text-[10px] md:col-span-2">{p.dosage}</span>
                    <span className="font-mono-label text-[10px] md:col-span-2">Stock: {p.stock}</span>
                    <span className="font-serif-display text-xl md:col-span-2">€{Number(p.price).toFixed(2)}</span>
                    <div className="flex gap-4 ml-auto md:col-span-2 md:justify-end">
                      <button data-testid={`edit-${p.slug}`} onClick={() => setEditing(p)} className="font-mono-label text-[10px] underline">EDIT</button>
                      <button onClick={() => del(p.id)} className="font-mono-label text-[10px] text-red-600 underline">DELETE</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {editing && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6">
                <div className="bg-white border border-neutral-300 max-w-2xl w-full max-h-[90vh] overflow-auto p-8">
                  <p className="font-mono-label text-[11px] text-neutral-500 mb-4">[ {editing.id ? "EDIT" : "NEW"} PRODUCT ]</p>
                  <h2 className="font-serif-display text-3xl mb-6">{editing.name || "New product"}</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ["slug", "Slug"], ["name", "Name"], ["dosage", "Dosage"],
                      ["accent", "Accent hex"], ["price", "Price"], ["stock", "Stock"], ["image_url", "Image URL"],
                    ].map(([k, l]) => (
                      <input key={k} placeholder={l} value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className="border border-neutral-300 px-3 py-2 col-span-2 md:col-span-1" />
                    ))}
                    <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="border border-neutral-300 px-3 py-2 col-span-2 md:col-span-1">
                      <option value="peptide">Peptide (vial)</option>
                      <option value="peptide_pen">Peptide (pen)</option>
                      <option value="bac_water">Bac Water</option>
                      <option value="accessory">Accessory / Kit</option>
                    </select>
                    <textarea placeholder="Short desc NL" value={editing.short_desc_nl ?? ""} onChange={(e) => setEditing({ ...editing, short_desc_nl: e.target.value })} className="border border-neutral-300 px-3 py-2 col-span-2" />
                    <textarea placeholder="Short desc EN" value={editing.short_desc_en ?? ""} onChange={(e) => setEditing({ ...editing, short_desc_en: e.target.value })} className="border border-neutral-300 px-3 py-2 col-span-2" />
                    <textarea placeholder="Long desc NL" value={editing.long_desc_nl ?? ""} onChange={(e) => setEditing({ ...editing, long_desc_nl: e.target.value })} className="border border-neutral-300 px-3 py-2 col-span-2 min-h-24" />
                    <textarea placeholder="Long desc EN" value={editing.long_desc_en ?? ""} onChange={(e) => setEditing({ ...editing, long_desc_en: e.target.value })} className="border border-neutral-300 px-3 py-2 col-span-2 min-h-24" />
                  </div>
                  <div className="flex gap-4 mt-6 justify-end">
                    <button onClick={() => setEditing(null)} className="btn-sharp">Cancel</button>
                    <button data-testid="admin-save-product" onClick={save} className="btn-solid">Save →</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "orders" && <OrdersPanel orders={orders} reload={loadAll} />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//   Orders panel — real-time tracking, filter tabs, recovery links
// ═══════════════════════════════════════════════════════════════════════
function OrdersPanel({ orders, reload }) {
  const [filter, setFilter] = useState("all");
  const [counts, setCounts] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);

  const refreshCounts = async () => {
    try {
      const { data } = await api.get("/admin/orders/counts");
      setCounts(data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    refreshCounts();
    if (!autoRefresh) return;
    const t = setInterval(() => { refreshCounts(); reload(); }, 30000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const PENDING_STATUSES = ["pending", "initiated", "pending_payment", "awaiting_payment"];
  const FAILED_STATUSES = ["failed", "cancelled", "expired"];

  const buckets = {
    all: (o) => true,
    paid_new: (o) => o.status === "paid",
    shipped: (o) => o.status === "shipped",
    delivered: (o) => o.status === "delivered",
    pending: (o) => PENDING_STATUSES.includes(o.status),
    failed: (o) => FAILED_STATUSES.includes(o.status),
  };

  const filtered = orders.filter(buckets[filter] || (() => true));

  const filters = [
    ["all", "Alles", counts.all ?? orders.length],
    ["paid_new", "Nieuwe betaalde", counts.paid_new ?? 0],
    ["shipped", "Verzonden", counts.shipped ?? 0],
    ["delivered", "Geleverd", counts.delivered ?? 0],
    ["pending", "Wacht op betaling", counts.pending ?? 0],
    ["failed", "Mislukt / geannuleerd", counts.failed ?? 0],
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="font-mono-label text-[10px] text-neutral-500">[ BESTELLINGEN · REAL-TIME ]</p>
        <div className="flex items-center gap-4">
          <button
            data-testid="scan-abandonments"
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/emails/abandonment/scan");
                toast.success(`${data.sent} herstel-emails verzonden · ${data.skipped} overgeslagen`);
              } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
            }}
            className="btn-sharp text-xs"
          >
            📧 SCAN CART-ABANDONS
          </button>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="accent-black" />
            <span className="font-mono-label text-[10px]">AUTO · 30s</span>
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {filters.map(([k, label, n]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
            className={`border px-3 py-2 font-mono-label text-[10px] ${filter === k ? "border-black bg-black text-white" : "border-neutral-300 bg-white hover:border-black"}`}
          >
            {label.toUpperCase()} · {n}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="py-10 text-center text-neutral-500">Geen bestellingen in deze categorie.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => <OrderCard key={o.id} o={o} reload={reload} isFailed={FAILED_STATUSES.includes(o.status)} />)}
        </div>
      )}
    </div>
  );
}

function OrderCard({ o, reload, isFailed }) {
  const [busy, setBusy] = useState(false);

  const act = async (path, method = "post", label) => {
    setBusy(true);
    try {
      await api[method](`/admin/orders/${o.id}${path}`);
      toast.success(label || "Updated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const sendRecovery = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/recovery-link`, { discount_pct: 5 });
      await navigator.clipboard.writeText(data.url);
      toast.success(`Recovery-link gekopieerd (5% korting → €${data.new_total.toFixed(2)})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const statusColor = {
    paid: "border-emerald-600 bg-emerald-50 text-emerald-800",
    shipped: "border-[#00529B] bg-blue-50 text-[#00529B]",
    delivered: "border-neutral-700 bg-neutral-100",
    failed: "border-[#D12A2F] bg-red-50 text-[#D12A2F]",
    cancelled: "border-[#D12A2F] bg-red-50 text-[#D12A2F]",
    expired: "border-neutral-400 bg-neutral-50 text-neutral-500",
  }[o.status] || "border-amber-500 bg-amber-50 text-amber-700";

  return (
    <div data-testid={`order-card-${o.id}`} className="border border-neutral-300 bg-white p-4 md:p-6">
      <div className="flex flex-wrap gap-2 items-baseline justify-between mb-3">
        <div>
          <p className="font-serif-display text-xl">#{o.id.slice(0, 8).toUpperCase()} · €{Number(o.total).toFixed(2)}</p>
          <p className="text-xs text-neutral-500">{o.created_at?.slice(0, 16).replace("T", " ")} — {o.shipping?.name || o.user_email || "guest"} · {o.shipping?.email || o.user_email}</p>
        </div>
        <span className={`px-2.5 py-1 border font-mono-label text-[10px] ${statusColor}`}>{o.status?.toUpperCase()}</span>
      </div>
      <div className="grid md:grid-cols-2 gap-4 border-t border-neutral-200 pt-3 text-sm">
        <div>
          <p className="font-mono-label text-[10px] text-neutral-500 mb-1">ITEMS</p>
          {(o.items || []).map((it, i) => (
            <p key={i}>{it.quantity}× {it.name} {it.dosage} — €{Number(it.line_total).toFixed(2)}</p>
          ))}
        </div>
        <div>
          <p className="font-mono-label text-[10px] text-neutral-500 mb-1">ADRES</p>
          <p>{o.shipping?.address}, {o.shipping?.postal} {o.shipping?.city} ({o.shipping?.country})</p>
          {o.reference && <p className="mt-2 font-mono text-xs text-[#00529B]">Ref: {o.reference}</p>}
          {o.tracking_number && <p className="mt-1 text-xs">Track: <a href={o.tracking_url} className="underline">{o.tracking_number}</a></p>}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 border-t border-neutral-200 pt-3">
        {(o.invoice_number || o.status === "paid" || o.status === "shipped" || o.status === "delivered") && (
          <a
            href={o.invoice_number ? `${import.meta.env?.VITE_BACKEND || ""}/api/invoices/${o.invoice_number}` : "#"}
            onClick={async (e) => {
              if (!o.invoice_number) {
                e.preventDefault();
                try {
                  const { data } = await api.post(`/admin/orders/${o.id}/invoice`);
                  toast.success(`Factuur ${data.number} aangemaakt`);
                  reload();
                  window.open(`/api/invoices/${data.number}`, "_blank");
                } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
              }
            }}
            target="_blank"
            rel="noreferrer"
            data-testid={`invoice-${o.id}`}
            className="font-mono-label text-[10px] tracking-[0.15em] border border-black px-3 py-2 hover:bg-black hover:text-white transition"
          >
            {o.invoice_number ? `📄 FACTUUR ${o.invoice_number}` : "📄 GENEREER FACTUUR"}
          </a>
        )}
        {o.status !== "paid" && !isFailed && (
          <button disabled={busy} onClick={() => act("/mark-paid", "post", "Marked paid")} className="btn-sharp text-xs">MARK PAID</button>
        )}
        {o.status === "paid" && (
          <button disabled={busy} onClick={() => act("/ship", "post", "Marked shipped")} className="btn-sharp text-xs">MARK SHIPPED</button>
        )}
        {o.status === "shipped" && (
          <button disabled={busy} onClick={() => act("/deliver", "post", "Marked delivered")} className="btn-sharp text-xs">MARK DELIVERED</button>
        )}
        {isFailed && (
          <button data-testid={`recovery-${o.id}`} disabled={busy} onClick={sendRecovery} className="btn-solid text-xs bg-[#D12A2F]">
            🎁 STUUR HERSTEL-LINK · 5% KORTING
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//   Administratie · financial report per period
// ═══════════════════════════════════════════════════════════════════════
function AccountingPanel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [period, setPeriod] = useState("YEAR");
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/accounting/report", { params: { year, period } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, [year, period]);

  const btnCls = (v) => `border px-3 py-2 font-mono-label text-[10px] ${period === v ? "border-black bg-black text-white" : "border-neutral-300 bg-white hover:border-black"}`;

  return (
    <div data-testid="accounting-panel" className="border border-neutral-300 bg-white p-6 md:p-8">
      <h2 className="font-serif-display text-3xl mb-2">Administratie</h2>
      <p className="text-sm text-neutral-600 mb-6">Geautomatiseerde omzet, BTW & Stripe-fees per periode.</p>

      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <label className="font-mono-label text-[10px] text-neutral-500">PERIODE</label>
        <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="border border-neutral-300 px-2 py-1 text-sm">
          {[2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {["Q1", "Q2", "Q3", "Q4", "H1", "H2", "YEAR"].map((p) => (
          <button key={p} onClick={() => setPeriod(p)} className={btnCls(p)}>{p === "YEAR" ? "HEEL JAAR" : p}</button>
        ))}
        <button onClick={load} className="btn-sharp text-xs ml-auto">↻ UPDATE</button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-neutral-300">
          {[
            ["OMZET (incl BTW)", `€${data.gross_incl_btw.toFixed(2)}`, `${data.orders} bestellingen`],
            ["BTW AF TE DRAGEN", `€${data.btw_to_pay.toFixed(2)}`, "21% BTW"],
            ["STRIPE FEES", `€${data.stripe_fees_est.toFixed(2)}`, "geschat 1.4% + €0.25"],
            ["NETTO (excl BTW & fees)", `€${data.netto_after_fees_and_btw.toFixed(2)}`, `${data.refunds > 0 ? `- ${data.refunds} refunds €${data.refunded.toFixed(2)}` : "geen refunds"}`],
          ].map(([l, v, sub]) => (
            <div key={l} className="border-r border-b border-neutral-300 p-5">
              <p className="font-mono-label text-[10px] text-neutral-500">{l}</p>
              <p className="font-serif-display text-2xl mt-1">{v}</p>
              <p className="text-[10px] text-neutral-500 mt-1">{sub}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//   Discount codes CRUD
// ═══════════════════════════════════════════════════════════════════════
function DiscountCodesPanel() {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", pct: 10, active: true, max_uses: "", valid_until: "", description: "" });
  const load = async () => {
    const { data } = await api.get("/admin/discount-codes");
    setCodes(data.codes);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/discount-codes", {
        ...form,
        pct: parseFloat(form.pct),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        valid_until: form.valid_until || null,
      });
      toast.success("Code created");
      setForm({ code: "", pct: 10, active: true, max_uses: "", valid_until: "", description: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggle = async (c) => {
    await api.put(`/admin/discount-codes/${c.code}`, { ...c, active: !c.active });
    load();
  };
  const del = async (code) => {
    if (!confirm(`Verwijder code ${code}?`)) return;
    await api.delete(`/admin/discount-codes/${code}`);
    load();
  };

  return (
    <div data-testid="codes-panel" className="border border-neutral-300 bg-white p-6 md:p-8">
      <h2 className="font-serif-display text-3xl mb-2">Kortingscodes</h2>
      <p className="text-sm text-neutral-600 mb-6">Maak persoonlijke codes die klanten bij checkout kunnen invoeren.</p>

      <div className="grid md:grid-cols-6 gap-2 mb-6">
        <input placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="border border-neutral-300 px-3 py-2 md:col-span-2 uppercase" />
        <input type="number" placeholder="% korting" value={form.pct} onChange={(e) => setForm({ ...form, pct: e.target.value })} className="border border-neutral-300 px-3 py-2" />
        <input type="number" placeholder="Max uses" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="border border-neutral-300 px-3 py-2" />
        <input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="border border-neutral-300 px-3 py-2" />
        <button onClick={create} className="btn-solid">+ AANMAKEN</button>
        <input placeholder="Beschrijving (optioneel)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-neutral-300 px-3 py-2 md:col-span-6" />
      </div>

      <div className="border-t border-neutral-300">
        {codes.length === 0 ? (
          <p className="py-6 text-center text-neutral-500 text-sm">Nog geen kortingscodes.</p>
        ) : codes.map((c) => (
          <div key={c.code} className="grid grid-cols-12 gap-2 border-b border-neutral-200 py-3 items-center text-sm">
            <span className="col-span-3 font-mono font-semibold">{c.code}</span>
            <span className="col-span-1 font-serif-display text-lg">−{c.pct}%</span>
            <span className="col-span-2 text-xs text-neutral-500">{c.uses}/{c.max_uses ?? "∞"} gebruikt</span>
            <span className="col-span-3 text-xs text-neutral-500 truncate">{c.description || "—"}</span>
            <div className="col-span-3 flex gap-2 justify-end">
              <button onClick={() => toggle(c)} className={`text-[10px] font-mono-label px-2 py-1 border ${c.active ? "border-emerald-600 text-emerald-700" : "border-neutral-300 text-neutral-500"}`}>
                {c.active ? "ACTIEF" : "INACTIEF"}
              </button>
              <button onClick={() => del(c.code)} className="text-[10px] font-mono-label text-[#D12A2F] hover:underline">VERWIJDEREN</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//   Site-wide sale
// ═══════════════════════════════════════════════════════════════════════
function SalePanel() {
  const [sale, setSale] = useState({ active: false, name: "", pct: 10, valid_until: "" });
  const load = async () => {
    try { const { data } = await api.get("/admin/sale"); setSale({ active: !!data.active, name: data.name || "", pct: data.pct || 0, valid_until: data.valid_until || "" }); } catch { /* empty */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/admin/sale", { ...sale, pct: parseFloat(sale.pct), valid_until: sale.valid_until || null });
      toast.success("Sale opgeslagen — klanten zien 'm direct");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="sale-panel" className="border border-neutral-300 bg-white p-6 md:p-8 max-w-2xl">
      <h2 className="font-serif-display text-3xl mb-2">Site Sale</h2>
      <p className="text-sm text-neutral-600 mb-6">Activeer een site-brede korting. Klanten zien een banner en de korting wordt automatisch toegepast in de checkout.</p>

      <label className="flex items-center gap-4 mb-6 cursor-pointer border-2 border-black p-4">
        <input type="checkbox" checked={sale.active} onChange={(e) => setSale({ ...sale, active: e.target.checked })} className="w-6 h-6 accent-black" data-testid="sale-active" />
        <div className="flex-1">
          <p className="font-serif-display text-xl">{sale.active ? "✓ Sale is ACTIEF" : "✗ Sale staat UIT"}</p>
          <p className="text-xs text-neutral-600">{sale.active ? "Klanten zien de rode banner + korting wordt toegepast." : "Geen banner, geen automatische korting."}</p>
        </div>
        <span className={`font-mono-label text-[10px] px-3 py-1.5 ${sale.active ? "bg-emerald-100 text-emerald-800 border border-emerald-600" : "bg-neutral-100 text-neutral-500 border border-neutral-400"}`}>
          {sale.active ? "AAN" : "UIT"}
        </span>
      </label>

      <div className="grid gap-3">
        <div>
          <p className="font-mono-label text-[10px] text-neutral-500 mb-1">SALE NAAM</p>
          <input placeholder="bv. Autumn Research Sale" value={sale.name} onChange={(e) => setSale({ ...sale, name: e.target.value })} className="w-full border border-neutral-300 px-3 py-2" data-testid="sale-name" />
        </div>
        <div>
          <p className="font-mono-label text-[10px] text-neutral-500 mb-1">KORTING %</p>
          <input type="number" min="0" max="90" value={sale.pct} onChange={(e) => setSale({ ...sale, pct: e.target.value })} className="w-full border border-neutral-300 px-3 py-2" data-testid="sale-pct" />
        </div>
        <div>
          <p className="font-mono-label text-[10px] text-neutral-500 mb-1">GELDIG TOT (optioneel)</p>
          <input type="date" value={sale.valid_until?.slice(0, 10) || ""} onChange={(e) => setSale({ ...sale, valid_until: e.target.value })} className="w-full border border-neutral-300 px-3 py-2" />
        </div>
        <button onClick={save} className="btn-solid mt-2" data-testid="sale-save">OPSLAAN →</button>
      </div>
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════
//   VIP customers auto-tier (≥3 paid orders)
// ═══════════════════════════════════════════════════════════════════════
function VipPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/vip/customers").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="text-neutral-500">Loading…</p>;
  return (
    <div data-testid="vip-panel" className="border border-neutral-300 bg-white p-6 md:p-8">
      <h2 className="font-serif-display text-3xl mb-2">VIP klanten</h2>
      <p className="text-sm text-neutral-600 mb-6">
        Klanten die <b>{data.threshold}+ betaalde bestellingen</b> hebben, krijgen automatisch <b>{data.discount_pct}% VIP-korting</b> bij elke volgende checkout. Geen actie nodig — het systeem detecteert het via hun e-mail.
      </p>
      <div className="border-t border-l border-neutral-300 grid grid-cols-2 md:grid-cols-3 mb-6">
        {[["AANTAL VIPs", data.count], ["THRESHOLD", `${data.threshold} orders`], ["AUTO-KORTING", `${data.discount_pct}%`]].map(([l, v]) => (
          <div key={l} className="border-r border-b border-neutral-300 p-5">
            <p className="font-mono-label text-[10px] text-neutral-500">{l}</p>
            <p className="font-serif-display text-2xl mt-1">{v}</p>
          </div>
        ))}
      </div>
      {data.customers.length === 0 ? (
        <p className="py-6 text-center text-neutral-500 text-sm">Nog geen VIP klanten — kom terug als je 3+ herhaal-orders hebt.</p>
      ) : (
        <div className="border-t border-neutral-300">
          {data.customers.map((c) => (
            <div key={c.email} className="grid grid-cols-12 border-b border-neutral-200 py-3 items-center text-sm">
              <span className="col-span-6 font-mono">{c.email}</span>
              <span className="col-span-3 text-neutral-500">{c.orders} orders</span>
              <span className="col-span-3 text-right font-serif-display">€{c.spent.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// END VIP
